import { useEffect, useMemo, useState } from "react"

import { tealiumInit } from "tealium"

import { Client } from "api/client"

import { ClientContext } from "context/ClientContext"

import { AuthRoute, AuthRoutes } from "utilities/constants"
import { log, SHOW_LOGS } from "utilities/logging"

export const ClientProvider = ({ children }: React.PropsWithChildren) => {
  const [client, setClient] = useState<Client>()
  const [isTealiumReady, setIsTealiumReady] = useState(false)

  useEffect(() => {
    if (client) {
      const currentPath = window.location.pathname.slice(1)
      // The `isAuthPage` flag determines the Tealium environment used for analytics.
      // If `isAuthPage` is true, Tealium uses the "prod" environment and the `collegeName` for its profile.
      // Otherwise, it uses `env.VITE_TEALIUM_TARGET_ENV` and a "sandbox" profile.
      // This ensures consistent production tracking for authentication flows, while allowing different environments for other pages.
      const isAuthPage = AuthRoutes.includes(currentPath as AuthRoute)

      const initializeTealium = async () => {
        try {
          if (client.theme === "base") {
            log(SHOW_LOGS.TEALIUM, "Tealium skipped for base theme")
            return
          }

          await tealiumInit({
            collegeName: client.theme,
            isAuthPage,
          })
          setIsTealiumReady(true)
          log(SHOW_LOGS.TEALIUM, "🪵 ✅ Tealium initialized")
        } catch (error) {
          log(SHOW_LOGS.TEALIUM, "🪵 ❌ Tealium failed to initialize:", error)
        }
      }

      initializeTealium()

      return () => {
        setIsTealiumReady(false)
      }
    }
  }, [client])

  const value = useMemo(
    () => ({ client, setClient, isTealiumReady }),
    [client, setClient, isTealiumReady],
  )

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  )
}
